const METRIKY_URL = "leftmenu/kmec"
const DISTRICT_BASE_URL = "kmec/"
const KMEC_GRAFY_BASE_URL = "kmec-grafy-mc/"
const METRIKA_DOWNLOAD = "metrika-data-download/"

export const useKmecStore = defineStore("kmec", () => {
    const mainStore = useMainStore()

    const selectedMetrikaKod = ref<string>("")
    const metrikyTree = ref<KimKmecMenuItem[]>([])
    const metrikyChartData = ref<Record<string, KmecMetrikaGraf>>({})
    const selectedMetrika = ref<Metrika | undefined>(undefined)
    const selectedDistrict = ref<string>("")
    const districtData = ref<
        Record<string, Record<string, KmecDistrictItem[]>>
    >({})
    const isFetchingDistrictData = ref<boolean>(false)
    const dashboardVisibleGroups = ref<string[]>([])
    const districtsForSearch = ref<{ id: string; name: string }[]>([])
    const districtsSearchInput = ref<string>("")
    const loadingTree = ref<boolean>(false)
    const loadingMetrikaFromUrl = ref<boolean>(false)
    const notFound = ref<boolean>(false)
    const searchInput = ref<string>("")
    const hasChartData = ref<boolean>(false)
    const noData = ref<boolean>(false)

    const allMetriky = computed(() => {
        const metriky: Metrika[] = []
        metrikyTree.value.forEach(oblast => {
            oblast.CELEK.forEach(celek => {
                celek.SKUPINAMETRIK.forEach(skupina => {
                    skupina.METRIKA.forEach(metrika => {
                        metriky.push(metrika)
                    })
                })
            })
        })
        return metriky
    })

    const filteredMetrikyTree = computed(() => {
        const oblasti: KimKmecMenuItem[] = []
        metrikyTree.value.forEach(oblast => {
            const celky: Celek[] = []
            const newOblast = { ...oblast }
            oblast.CELEK.forEach(celek => {
                const skupiny: Skupinametrik[] = []
                const newCelek = { ...celek }
                celek.SKUPINAMETRIK.forEach(skupina => {
                    const metriky: Metrika[] = []
                    const newSkupina = { ...skupina }
                    skupina.METRIKA.forEach(metrika => {
                        if (searchInput.value.length > 2) {
                            if (
                                metrika.M_NAZEV.toLowerCase().includes(
                                    searchInput.value.toLowerCase()
                                ) ||
                                metrika.M_KOD.toLowerCase().includes(
                                    searchInput.value.toLowerCase()
                                ) ||
                                metrika.SM_NAZEV.toLowerCase().includes(
                                    searchInput.value.toLowerCase()
                                )
                            ) {
                                metriky.push(metrika)
                            }
                        } else {
                            metriky.push(metrika)
                        }
                    })
                    if (metriky.length > 0) {
                        newSkupina.METRIKA = metriky
                        skupiny.push(newSkupina)
                    }
                })
                if (skupiny.length > 0) {
                    newCelek.SKUPINAMETRIK = skupiny
                    celky.push(newCelek)
                }
            })
            if (celky.length > 0) {
                newOblast.CELEK = celky
                if (newOblast.KOD !== "0000") {
                    oblasti.push(newOblast)
                }
            }
        })
        return oblasti
    })

    const selectedDistrictData = computed(() => {
        if (!selectedDistrict.value) return null
        return districtData.value[selectedDistrict.value] || null
    })

    const selectedDistrictName = computed(() => {
        if (
            !selectedDistrict.value ||
            !districtData.value ||
            !districtData.value[selectedDistrict.value]
        )
            return null
        const data = districtData.value[selectedDistrict.value] as Record<
            string,
            KmecDistrictItem[]
        >
        const firstMetrikaKey = Object.keys(data)[0] as string
        return (
            (data[firstMetrikaKey] as KmecDistrictItem[])[0]?.objekt_nazev ||
            null
        )
    })

    const metrikaTitle = computed(() => {
        if (!selectedMetrika.value) return ""
        const metrikaNazev = useLocaleValue(selectedMetrika.value, "M_NAZEV")
        const skupinaNazev = useLocaleValue(selectedMetrika.value, "SM_NAZEV")
        if (skupinaNazev.replace("\n", "") === metrikaNazev) {
            return metrikaNazev
        }
        return `${skupinaNazev} — ${metrikaNazev}`
    })

    const filteredSearchDistricts = computed(() => {
        //if (districtsSearchInput.value.length < 3) return []
        return districtsForSearch.value.filter(district =>
            district.name
                .toLowerCase()
                .includes(districtsSearchInput.value.toLowerCase())
        )
    })

    const fetchMetriky = async () => {
        loadingTree.value = true
        const data = await $fetch<KimKmecMenuItem[]>(METRIKY_URL, {
            baseURL: import.meta.env.SSR
                ? mainStore.apiBaseUrlInt
                : mainStore.apiBaseUrl,
        })
        metrikyTree.value = data || []
        loadingTree.value = false
        return metrikyTree.value
    }

    const setSelectedDistrict = async (kod: string) => {
        if (districtData.value[kod]) {
            selectedDistrict.value = kod
            dashboardVisibleGroups.value = []
        } else {
            const data = await fetchDistrictData(kod)
            if (data) {
                selectedDistrict.value = kod
                dashboardVisibleGroups.value = []
            }
        }
    }

    const fetchDistrictData = async (kod: string) => {
        if (districtData.value[kod]) {
            return districtData.value[kod]
        } else {
            isFetchingDistrictData.value = true
            const data = await $fetch<KmecDistrictItem[][]>(
                DISTRICT_BASE_URL + kod,
                {
                    baseURL: import.meta.env.SSR
                        ? mainStore.apiBaseUrlInt
                        : mainStore.apiBaseUrl,
                }
            )
            if (!data || data.length === 0) {
                isFetchingDistrictData.value = false
                console.warn("Nepodařilo se načíst data")
                return null
            }
            const newData: Record<string, KmecDistrictItem[]> = {}
            let tileGroupId = ""
            data?.[0]?.forEach(metrika => {
                let oblastId = ""
                if (!metrika.metrika_cislo) {
                    return
                }
                try {
                    oblastId = metrika.metrika_cislo.split(".")[1] as string
                } catch (error) {
                    console.log("Error parsing oblastId", error)
                    console.log(metrika)
                    return
                }
                if (oblastId !== tileGroupId) {
                    tileGroupId = oblastId
                    newData[tileGroupId] = []
                }
                newData[tileGroupId]?.push(metrika)
            })
            districtData.value[kod] = newData
            isFetchingDistrictData.value = false
            return districtData.value[kod]
        }
    }

    const setSelectedMetrika = async (kod: string) => {
        selectedMetrikaKod.value = kod
        // check if kod is skupina metrik or metrika
        selectedMetrika.value = await findMetrika(kod)
        if (selectedMetrika.value) {
            try {
                await fetchMetrikaChartData(kod)
            } catch (error) {
                console.log(error)
            }
        }
    }

    const fetchMetrikaChartData = async (kod: string) => {
        loadingMetrikaFromUrl.value = true
        try {
            const data = await $fetch<KmecMetrikaGraf[]>(
                KMEC_GRAFY_BASE_URL + kod,
                {
                    baseURL: import.meta.env.SSR
                        ? mainStore.apiBaseUrlInt
                        : mainStore.apiBaseUrl,
                }
            )
            if (
                data &&
                data.length > 0 &&
                data[0] &&
                data[0].hodnoty.length > 0
            ) {
                metrikyChartData.value[kod] = data[0] as KmecMetrikaGraf
                hasChartData.value = true
                noData.value = false
                return data[0]
            } else {
                hasChartData.value = true
                noData.value = true
                console.warn("Žádné data pro metriku:", kod)
                return undefined
            }
        } catch (error) {
            console.log(error)
        } finally {
            loadingMetrikaFromUrl.value = false
        }
    }

    const findMetrika = async (kod: string): Promise<Metrika | undefined> => {
        const oblastKod = kod.split(".")[1]
        const celekKod = oblastKod + "." + kod.split(".")[2]
        const skupinaKod =
            oblastKod + "." + kod.split(".")[2] + "." + kod.split(".")[3]
        if (metrikyTree.value.length === 0) {
            await fetchMetriky()
        }
        const oblast = metrikyTree.value.find(oblast => {
            return oblast.KOD === oblastKod
        })
        if (!oblast) {
            notFound.value = true
            return
        }
        const celek = oblast.CELEK.find(celek => {
            return celek.KOD === celekKod
        })
        if (!celek) {
            notFound.value = true
            return
        }
        const skupinaMetrik = celek.SKUPINAMETRIK.find(skupina => {
            return skupina.KOD === "m." + skupinaKod
        })
        if (!skupinaMetrik) {
            notFound.value = true
            return
        }
        const metrika = skupinaMetrik.METRIKA.find(metrika => {
            return metrika.M_KOD === kod
        })
        return metrika
    }

    const findSkupinaMetrik = (kod: string): Skupinametrik | undefined => {
        const oblastKod = kod.split(".")[1]
        const celekKod = oblastKod + "." + kod.split(".")[2]
        const skupinaKod =
            oblastKod + "." + kod.split(".")[2] + "." + kod.split(".")[3]
        if (metrikyTree.value.length === 0) {
            return
        }
        const oblast = metrikyTree.value.find(oblast => {
            return oblast.KOD === oblastKod
        })
        if (!oblast) {
            return
        }
        const celek = oblast.CELEK.find(celek => {
            return celek.KOD === celekKod
        })
        if (!celek) {
            return
        }
        const skupinaMetrik = celek.SKUPINAMETRIK.find(skupina => {
            return skupina.KOD === "m." + skupinaKod
        })
        return skupinaMetrik
    }

    const fetchMetrikaDataForDownload =
        async (): Promise<MetrikaDownloadData> => {
            const mainStore = useMainStore()
            const data = await $fetch<MetrikaDownloadData>(
                METRIKA_DOWNLOAD + selectedMetrikaKod.value,
                {
                    baseURL: import.meta.env.SSR
                        ? mainStore.apiBaseUrlInt
                        : mainStore.apiBaseUrl,
                }
            )
            return data
        }

    return {
        selectedMetrikaKod,
        metrikyTree,
        metrikyChartData,
        selectedMetrika,
        selectedDistrict,
        districtData,
        isFetchingDistrictData,
        dashboardVisibleGroups,
        districtsForSearch,
        districtsSearchInput,
        loadingTree,
        loadingMetrikaFromUrl,
        notFound,
        searchInput,
        hasChartData,
        noData,
        allMetriky,
        filteredMetrikyTree,
        selectedDistrictData,
        selectedDistrictName,
        metrikaTitle,
        filteredSearchDistricts,
        fetchMetriky,
        setSelectedDistrict,
        fetchDistrictData,
        setSelectedMetrika,
        fetchMetrikaChartData,
        findMetrika,
        findSkupinaMetrik,
        fetchMetrikaDataForDownload,
    }
})

if ((import.meta as MetaHot).hot) {
    ;(import.meta as MetaHot).hot?.accept(
        acceptHMRUpdate(useKmecStore, (import.meta as MetaHot).hot)
    )
}
